import Joi from 'joi';
import Mustache from 'mustache';
import { markup2Html, markupCompatible } from '@api-consent/util/markup';

// Examples:
// (1) 'simple text'
// (2) [{ text: 'foo', langs: [], sps: [] }]
// (3) [{ text: 'foo', langs: ['de'], sps: [] }, { text: 'bar', langs: ['tr'], sps: [] }]

const MultiTextSchemaItem = Joi.object().keys({
  text: Joi.string().required().allow(''),
  langs: Joi.array().items(Joi.string()),
  sps: Joi.array().items(Joi.string()),
});

const MultiTextSchema = Joi.array().required().items(MultiTextSchemaItem.required());

export interface MultiTextItem {
  text: string;
  langs?: string[];
  sps?: string[];
}

export type MultiTextSource = string | MultiTextItem[];

export type TextFormat = 'html' | 'markup';

export interface MultiTextRenderOptions {
  spId?: string;
  lang?: string;
  context?: unknown;
  textformat?: TextFormat;
  fallbackText?: string;
  textformatCompatibility?: boolean;
}

export default class MultiText {
  data: MultiTextItem[];

  constructor(source: MultiTextSource) {
    const array = typeof source === 'string' ? [{ text: source }] : source;
    const { error } = MultiTextSchema.validate(array);
    if (error) throw error; // ValidationError
    this.data = array
      .map(({ text, langs = [], sps = [] }) => ({
        text,
        langs,
        sps,
      }));
  }

  render({
    spId, lang, context = {}, textformat, fallbackText,
    textformatCompatibility: compatibility = false, // workaround
  }: MultiTextRenderOptions): string | undefined {
    // filter texts by service provider and/or language
    const spec = this.data.filter((item) => (
      (!item.langs?.length || (lang && item.langs?.includes(lang)))
      && (!item.sps?.length || (spId && item.sps?.includes(spId)))
    ))[0];
    // pick the right fallback
    const text = spec?.text ?? fallbackText;
    if (!text) return undefined; // no match found
    // pick the right textformat
    if (textformat === 'html') {
      return markup2Html(text, { context, compatibility });
    }
    const rendered = Mustache.render(text, context);
    return compatibility ? markupCompatible(rendered) : rendered;
  }
}
